// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.25.0 AC6 / AC7 (idea-catalog #276) — which register reads which
 * boundary-statement tag, and which selector scopes it.
 *
 * ## Why this is in shared
 *
 * A boundary statement is pack guidance carrying a tag. A register renders it
 * only when the framework that authored it also DECLARES that register's kinds
 * (62.21.0 AC4, `installedDeclaringFrameworkCodes`). Nothing at install time
 * enforced that coupling, so a pack could author a statement under framework X,
 * declare no kind under X, install cleanly, and the register would render "no
 * installed framework declares a boundary statement" over a pack that authors
 * one.
 *
 * Three places need the reader → tag → selector map: the reader services
 * themselves (each passes its own entry's selectors to the scoped read),
 * `validate:packs` (AC6, over all three installer roots) and the installer's
 * non-fatal `unscoped_boundary_statements` report (AC7). The validator and the
 * installer cannot import backend services, so the map lives here and the
 * services import it. The backend corpus test asserts **map ⇔ call sites** in
 * both directions, so a reader added without an entry — or an entry whose
 * service no longer reads — fails by name.
 *
 * ## Two discovery mechanisms
 *
 * - `deadline_event_type` — the register's kinds are declared by installed
 *   deadline records carrying `event_type`; the service reads through
 *   `scopedBoundaryStatements` (`installedDeclaringFrameworkCodes`).
 * - `form_register_surface` — the register's kinds are installed FORM TEMPLATES
 *   carrying `register_surface`; no pack declares a deadline `event_type` for
 *   them, so the deadline join would return nothing. The service scopes by its
 *   own catalogue of those templates.
 *
 * ## Tags shared across readers
 *
 * `platform_boundary` and `scope_boundary` are read by several registers. A
 * statement under a shared tag is reachable when ANY reader of that tag has a
 * declaring record under the statement's framework code, so consumers union
 * selectors by tag rather than taking one reader's.
 *
 * ## Out of this map, deliberately
 *
 * Three other services expose a `boundaryStatements` read —
 * `investor-protection` (it borrows the sector-licence reader, so its tags are
 * scoped through that entry), `regulatory-classification` and
 * `regulatory-placeholders` (both unscoped `DeclaredRecordReader.statements`
 * reads over their own tag, with no register-kind selector to couple to). The
 * corpus test's call-site census governs every call whose tag is one of the
 * tags below; theirs are not.
 *
 * Every value below is an existing shared constant; nothing here names a tag or
 * a selector of its own.
 */
import { AML_PROGRAMME_BOUNDARY_TAG, AML_REGISTER_SURFACES } from './aml-programme.js';
import { VEHICLE_MONITORING_BOUNDARY_TAG, VEHICLE_MONITORING_SURFACES } from './vehicle-monitoring.js';
import { BIOMETRIC_CAPTURE_BOUNDARY_TAG, BIOMETRIC_CAPTURE_REGISTER_SURFACES, } from './biometric-capture.js';
import { AUTHORITY_DIRECTION_BOUNDARY_GUIDANCE_TAG, AUTHORITY_EVENT_CLASSIFICATION_EVENT_TYPE, } from './authority-operations.js';
import { CREDIT_INFORMATION_USE_BOUNDARY_TAG, CREDIT_INFORMATION_USE_REGISTER_SURFACE, } from './credit-information.js';
import { CREDIT_INFORMATION_SUPPLY_BOUNDARY_TAG, CREDIT_INFORMATION_SUPPLY_EVENT_TYPE, } from './credit-information-supply.js';
import { DATA_SERVICE_PROVIDER_BOUNDARY_TAG, DATA_SERVICE_PROVIDER_EVENT_TYPE, } from './data-services.js';
import { ISSUED_RECORD_GUIDANCE_TAGS, ISSUED_RECORD_LIFECYCLE_EVENT_TYPE, } from './issued-records.js';
import { MARKETING_COMMUNICATIONS_BOUNDARY_TAG, MARKETING_COMMUNICATIONS_EVENT_TYPE, } from './marketing-communications.js';
import { PCF_SUPERVISION_BOUNDARY_TAG, PCF_SUPERVISION_REGISTER_SURFACES, } from './pcf-supervision.js';
import { DATA_EXPLOITATION_BOUNDARY_TAG, DATA_EXPLOITATION_REGISTER_SURFACES, } from './data-exploitation.js';
import { CYBERSECURITY_ASSURANCE_INDEX_BOUNDARY_TAG, CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES, } from './cybersecurity-assurance-index.js';
import { PRODUCT_CLASSIFICATION_REGISTER_SURFACE } from './product-assurance.js';
import { CUSTOMS_TAX_BOUNDARY_TAG, CUSTOMS_TAX_REGISTER_SURFACES, } from './customs-tax-administration.js';
import { PUBLICATION_ASSESSMENT_CATEGORY_EVENT_TYPE, PUBLICATION_GUIDANCE_TAGS, PUBLICATION_RELEASE_GATE_EVENT_TYPE, } from './publishing.js';
import { BOUNDARY_GUIDANCE_TAGS, SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE } from './sector-licensing.js';
/** How a reader discovers which frameworks declare its register's kinds. */
export const BOUNDARY_STATEMENT_DISCOVERY_MODES = [
    'deadline_event_type',
    'form_register_surface',
];
/**
 * The boundary-statement readers, one per service (nine measured 2026-09-25
 * against the backend call sites; the three marketing registers share one
 * service, one tag and one selector, so they are one reader). Sprint 62.46.0
 * added the tenth, the AML programme register, which discovers its declaring
 * frameworks by the six register surfaces its templates carry.
 */
export const BOUNDARY_STATEMENT_READERS = [
    {
        reader_id: 'sector-licensing',
        module_file: 'sector-licensing/licence-service.ts',
        tags: Object.values(BOUNDARY_GUIDANCE_TAGS),
        selectors: [SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'issued-records',
        module_file: 'issued-records/issued-record-kind-service.ts',
        tags: Object.values(ISSUED_RECORD_GUIDANCE_TAGS),
        selectors: [ISSUED_RECORD_LIFECYCLE_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'publishing',
        module_file: 'publishing/publication-kind-service.ts',
        tags: Object.values(PUBLICATION_GUIDANCE_TAGS),
        selectors: [PUBLICATION_RELEASE_GATE_EVENT_TYPE, PUBLICATION_ASSESSMENT_CATEGORY_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'authority-operations',
        module_file: 'authority-operations/direction-service.ts',
        tags: [AUTHORITY_DIRECTION_BOUNDARY_GUIDANCE_TAG],
        selectors: [AUTHORITY_EVENT_CLASSIFICATION_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'data-services',
        module_file: 'data-services/provider-service.ts',
        tags: [DATA_SERVICE_PROVIDER_BOUNDARY_TAG],
        selectors: [DATA_SERVICE_PROVIDER_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'credit-information-supply',
        module_file: 'credit-information-supply/supply-service.ts',
        tags: [CREDIT_INFORMATION_SUPPLY_BOUNDARY_TAG],
        selectors: [CREDIT_INFORMATION_SUPPLY_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'marketing-communications',
        module_file: 'marketing-communications/marketing-communications-service.ts',
        tags: [MARKETING_COMMUNICATIONS_BOUNDARY_TAG],
        selectors: [MARKETING_COMMUNICATIONS_EVENT_TYPE],
        discovery: 'deadline_event_type',
        read_path: 'scopedBoundaryStatements',
    },
    {
        reader_id: 'product-assurance',
        module_file: 'product-assurance/product-classification-service.ts',
        tags: [BOUNDARY_GUIDANCE_TAGS.platformBoundary],
        selectors: [PRODUCT_CLASSIFICATION_REGISTER_SURFACE],
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    {
        reader_id: 'credit-information',
        module_file: 'credit-information/credit-information-service.ts',
        tags: [CREDIT_INFORMATION_USE_BOUNDARY_TAG],
        selectors: [CREDIT_INFORMATION_USE_REGISTER_SURFACE],
        discovery: 'form_register_surface',
        read_path: 'boundaryStatementsForFrameworks',
    },
    {
        reader_id: 'aml-programme',
        module_file: 'aml-programme/aml-scoping-service.ts',
        tags: [AML_PROGRAMME_BOUNDARY_TAG],
        selectors: Object.values(AML_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    // Sprint 62.50.0 — the supervision-information registers, which discover their
    // declaring frameworks by the nine register surfaces their templates carry.
    {
        reader_id: 'pcf-supervision',
        module_file: 'pcf-supervision/pcf-vocabulary.ts',
        tags: [PCF_SUPERVISION_BOUNDARY_TAG],
        selectors: Object.values(PCF_SUPERVISION_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    // Sprint 62.51.0 — the data-exploitation registers, which discover their
    // declaring frameworks by the register surfaces their templates carry.
    {
        reader_id: 'data-exploitation',
        module_file: 'data-exploitation/de-vocabulary.ts',
        tags: [DATA_EXPLOITATION_BOUNDARY_TAG],
        selectors: Object.values(DATA_EXPLOITATION_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    // Sprint 62.52.0 — the cybersecurity assurance index registers, which discover
    // their declaring frameworks by the four register surfaces their templates carry.
    {
        reader_id: 'cybersecurity-assurance-index',
        module_file: 'cybersecurity-assurance-index/csai-vocabulary.ts',
        tags: [CYBERSECURITY_ASSURANCE_INDEX_BOUNDARY_TAG],
        selectors: Object.values(CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    // Sprint 62.55.0 — the customs tax-administration registers, which discover
    // their declaring frameworks by the ten register surfaces their templates carry.
    {
        reader_id: 'customs-tax-administration',
        module_file: 'customs-tax-administration/cta-vocabulary.ts',
        tags: [CUSTOMS_TAX_BOUNDARY_TAG],
        selectors: Object.values(CUSTOMS_TAX_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    // Sprint 62.56.0 — the biometric capture registers, which discover their declaring
    // frameworks by the three register surfaces their templates carry.
    {
        reader_id: 'biometric-capture',
        module_file: 'biometric-capture/biometric-vocabulary.ts',
        tags: [BIOMETRIC_CAPTURE_BOUNDARY_TAG],
        selectors: Object.values(BIOMETRIC_CAPTURE_REGISTER_SURFACES),
        discovery: 'form_register_surface',
        read_path: 'DeclaredRecordReader.statements',
    },
    {
        reader_id: 'vehicle-monitoring',
        module_file: 'vehicle-monitoring/device-vocabulary.ts',
        tags: [VEHICLE_MONITORING_BOUNDARY_TAG],
        selectors: [VEHICLE_MONITORING_SURFACES.device],
        discovery: 'form_register_surface',
        read_path: 'boundaryStatementsForFrameworks',
    },
];
/** Look up one reader's entry by id. Throws on an id the map does not hold. */
export function boundaryStatementReader(readerId) {
    const entry = BOUNDARY_STATEMENT_READERS.find((r) => r.reader_id === readerId);
    if (!entry)
        throw new Error(`No boundary-statement reader '${readerId}'`);
    return entry;
}
/**
 * Tag → every selector that some reader of the tag joins on (the union across
 * readers that share a tag), and tag → the reader ids that read it.
 */
export function boundaryStatementSelectorsByTag() {
    const out = new Map();
    for (const reader of BOUNDARY_STATEMENT_READERS) {
        for (const tag of reader.tags) {
            const entry = out.get(tag) ?? {
                selectors: new Set(),
                discovery: new Set(),
                readers: [],
            };
            for (const selector of reader.selectors)
                entry.selectors.add(selector);
            entry.discovery.add(reader.discovery);
            entry.readers.push(reader.reader_id);
            out.set(tag, entry);
        }
    }
    return out;
}
//# sourceMappingURL=boundary-statement-readers.js.map